// src/pages/TrackBooking.jsx
import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import RecenterButton from "../components/RecenterButton";
import LiveVehicleMarker from "../components/LiveVehicleMarker";
import { startTracking, stopTracking, getCurrentRoute } from "../services/trackingApi";
import EmployeeHeader from "../components/EmployeeHeader";
import EmployeeFooter from "../components/EmployeeFooter";
import { useDispatch, useSelector } from "react-redux";
import trackingWS from "../services/trackingWebSocket";
import bookingWS from "../services/bookingWebSocket";  // 🔴 For real-time status updates
import api from "../services/api";
import { toast } from "react-toastify";  // For status change notifications
import { fetchBookings } from "../actions/BookingActions";
import { fetchVehicles } from "../actions/VehicleActions";
import { DEFAULT_MAP_CENTER } from "../constants/mapDefaults";

const DEFAULT_CENTER = DEFAULT_MAP_CENTER;

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons
const startIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

const endIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// 🔴 NEW: Fetch route from OpenRouteService
const fetchRoute = async (start, end) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey) return null;
  try {
    const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;
    const res = await fetch(url);
    const data = await res.json();
    const coords = data.features?.[0]?.geometry?.coordinates;
    if (coords) {
      return coords.map(([lng, lat]) => [lat, lng]);
    }
  } catch (e) {
    console.error("Route fetch error:", e);
  }
  return null;
};

// 🔴 NEW: Fit map bounds to show route (only once initially)
const FitBounds = ({ bounds }) => {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (bounds && bounds.length >= 2 && !hasFitted.current) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      hasFitted.current = true; // Only fit once
    }
  }, [bounds, map]);
  return null;
};

// Auto-center map (only initially, not on every location update)
const MapController = ({ center, autoCenter = true }) => {
  const map = useMap();
  const hasCentered = useRef(false);

  useEffect(() => {
    if (center && autoCenter && !hasCentered.current) {
      map.flyTo(center, 15, { duration: 1.8 });
      hasCentered.current = true; // Only center once initially
    }
  }, [center, autoCenter, map]);
  return null;
};

export default function TrackBooking({ bid }) {
  const dispatch = useDispatch();
  
  // 🔴 FIX: Define helper functions to dispatch Redux actions
  const getBookings = async () => dispatch(fetchBookings());
  const getVehicles = async () => dispatch(fetchVehicles());

  const [start, setStart] = useState(null);  // Pickup location
  const [end, setEnd] = useState(null);      // Destination location
  const [currentPos, setCurrentPos] = useState(null);
  
  // 🔴 EXACTLY LIKE DRIVERTRIP: Simple useState for routes
  const [routeToPickup, setRouteToPickup] = useState([]);
  const [routeToDestination, setRouteToDestination] = useState([]);
  const lastRouteRefresh = useRef(0); // Throttle like DriverTrip
  const [distance, setDistance] = useState("—");
  const [duration, setDuration] = useState("—");
  // const [bId, setBId] = useState("");
  const [vId, setVId] = useState("Loading...");
  const [dSNo, setDSNo] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [bookingStatus, setBookingStatus] = useState("P"); // P/A/EN/IP/CO
  const [driverInitialLat, setDriverInitialLat] = useState(null);
  const [driverInitialLng, setDriverInitialLng] = useState(null);
  const [vehicleType, setVehicleType] = useState("Car"); // Vehicle type for icon
  
  // 🔴 NEW: Loading and error states for API fetch
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [bookingData, setBookingData] = useState(null);  // Full booking object

  // 🔴 NEW: Determine which locations to show on map based on status
  const getMapMarkers = () => {
    const status = bookingStatus.toUpperCase();
    
    // 🔴 FIX: Always show both pickup and destination markers like DriverTrip
    // This prevents markers from disappearing during status changes
    if (status === "A" || status === "ACTIVE" || status === "EN" || status === "ENROUTE") {
      // En route to pickup: Show both pickup and destination
      return {
        showPickup: true,
        showDestination: true, // 🔴 CHANGED: Always show destination
        targetLocation: start  // Heading to pickup
      };
    } else if (status === "IP" || status === "INPROGRESS") {
      // In progress: Show both pickup and destination
      return {
        showPickup: true, // 🔴 CHANGED: Always show pickup
        showDestination: true,
        targetLocation: end  // Heading to destination
      };
    } else if (status === "CO" || status === "COMPLETED") {
      // Completed: Show both
      return {
        showPickup: true,
        showDestination: true,
        targetLocation: end
      };
    }
    
    // Default: Show both markers
    return {
      showPickup: true,
      showDestination: true, // 🔴 CHANGED: Always show destination
      targetLocation: start
    };
  };

  // 🔴 NEW: Parse only bId from URL, then fetch booking details from API
  useEffect(() => {
    // const params = new URLSearchParams(window.location.search);
    // const bid = params.get("bId");

    if (!bid) {
      setLoadError("No booking ID provided");
      setIsLoading(false);
      return;
    }

    //setBId(bid);

    // Fetch booking details from API
    const fetchBookingDetails = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        
        console.log(`[TrackBooking] Fetching booking details for bId: ${bid}`);
        const bookings = await getBookings();
        
        // Find the specific booking by bId
        const booking = bookings.find(b => b.bId?.toString() === bid.toString());
        
        if (!booking) {
          setLoadError(`Booking #${bid} not found`);
          setIsLoading(false);
          return;
        }
        
        console.log(`[TrackBooking] ✅ Booking found:`, booking);
        
        // Store full booking data
        setBookingData(booking);
        
        // Set all states from API response (fresh data!)
        setVId(booking.vId || "N/A");
        setDSNo(booking.dSNo || "");
        setBookingStatus(booking.status || "P");
        
        // Fetch vehicle type from vehicle details API
        try {
          const vehicles = await getVehicles();
          const vehicle = vehicles.find(v => v.vehicleId?.toString() === booking.vId?.toString());
          if (vehicle?.vehicleType) {
            console.log(`[TrackBooking] ✅ Vehicle type found: ${vehicle.vehicleType}`);
            setVehicleType(vehicle.vehicleType);
          }
        } catch (vErr) {
          console.error("[TrackBooking] Failed to fetch vehicle type:", vErr);
        }
        
        // Set locations
        if (booking.slatitude && booking.slongitude) {
          setStart([parseFloat(booking.slatitude), parseFloat(booking.slongitude)]);
        }
        if (booking.elatitude && booking.elongitude) {
          setEnd([parseFloat(booking.elatitude), parseFloat(booking.elongitude)]);
        }
        
        // Set driver initial position
        if (booking.dlatitude && booking.dlongitude) {
          const dLat = parseFloat(booking.dlatitude);
          const dLng = parseFloat(booking.dlongitude);
          setDriverInitialLat(dLat);
          setDriverInitialLng(dLng);
          setCurrentPos({ lat: dLat, lng: dLng });
        }
        
        setIsLoading(false);
        
        // Start tracking system
        startTracking(bid, (pos) => {
          if (pos && pos.lat && pos.lng) {
            setCurrentPos({ lat: pos.lat, lng: pos.lng });
          }
        });
        
      } catch (error) {
        console.error("[TrackBooking] Error fetching booking:", error);
        setLoadError("Failed to load booking details");
        setIsLoading(false);
      }
    };

    fetchBookingDetails();

    return () => {
      stopTracking();
    };
  }, []);

  // 🔴 EXACTLY LIKE DRIVERTRIP: loadRoutes function
  const loadRoutes = async () => {
    if (!start || !end) {
      console.log('[TrackBooking] loadRoutes: Missing start or end coordinates');
      return;
    }

    console.log('[TrackBooking] loadRoutes called with:', { start, end, driverInitialLat, driverInitialLng });
    
    // Route from driver initial position to pickup
    if (driverInitialLat && driverInitialLng) {
      const toPickup = await fetchRoute([driverInitialLat, driverInitialLng], start);
      if (toPickup) {
        setRouteToPickup(toPickup);
        console.log(`[TrackBooking] ✅ Route to pickup SET: ${toPickup.length} points`);
      } else {
        console.log('[TrackBooking] ❌ Failed to load route to pickup');
      }
    }

    // Route from pickup to destination
    const toDest = await fetchRoute(start, end);
    if (toDest) {
      setRouteToDestination(toDest);
      console.log(`[TrackBooking] ✅ Route to destination SET: ${toDest.length} points`);
      
      // Calculate distance and duration
      const apiKey = process.env.REACT_APP_ORS_KEY;
      const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;
      try {
        const res = await fetch(url);
        const data = await res.json();
        if (data.features?.[0]) {
          const props = data.features[0].properties;
          setDistance((props.summary.distance / 1000).toFixed(2));
          setDuration(Math.ceil(props.summary.duration / 60));
        }
      } catch (e) {
        console.error('[TrackBooking] Distance calc error:', e);
      }
    }
  };

  // 🔴 EXACTLY LIKE DRIVERTRIP: Load routes when coordinates are ready (throttled)
  useEffect(() => {
    const now = Date.now();
    if (!start || !end || !driverInitialLat || !driverInitialLng) return;
    if (now - lastRouteRefresh.current < 30000) return; // throttle 30s like DriverTrip
    lastRouteRefresh.current = now;
    loadRoutes();
  }, [start, end, driverInitialLat, driverInitialLng]);

  // Real-time subscription: connect and subscribe to vId via trackingWS (using location WebSocket)
  const { authKey } = useSelector((state) => state.auth);
  useEffect(() => {
    if (!vId || vId === "Loading..." || !bid) return;
    
    // Connect to BOTH WebSockets
    if (authKey) {
      trackingWS.connect(authKey);
      
      // 🔴 FIX: Use connectAsEmployee for tracking page (not driver connect)
      // This properly handles status update messages for employees
      bookingWS.connectAsEmployee(authKey);
      
      // Check connection status periodically
      const checkConnection = setInterval(() => {
        setWsConnected(trackingWS.isConnected());
      }, 1000);
      
      // Initial check
      setTimeout(() => setWsConnected(trackingWS.isConnected()), 500);
      
      // 🔴 Listen for booking status updates from driver
      bookingWS.onStatusUpdate((statusUpdate) => {
        console.log(`[TrackBooking] 📥 Raw status update received:`, statusUpdate);
        // Only show toast if this employee is the bookingSno
        const { bookingSno } = statusUpdate;
        const { userDetails } = (typeof window !== 'undefined' && window.store && window.store.getState) ? window.store.getState().auth : {};
        if (
          bookingSno && userDetails?.ServiceNo &&
          bookingSno.toString() !== userDetails.ServiceNo.toString()
        ) {
          return; // Not for this employee
        }
        // Check if this update is for our booking
        if (statusUpdate && statusUpdate.bId && statusUpdate.bId.toString() === bid.toString()) {
          const newStatus = statusUpdate.status;
          console.log(`[TrackBooking] ✅ Status update for THIS booking: ${newStatus}`);
          
          // Update booking status
          setBookingStatus(newStatus);
          
          // Update driver location if provided  
          if (statusUpdate.dlatitude && statusUpdate.dlongitude) {
            setCurrentPos({
              lat: parseFloat(statusUpdate.dlatitude),
              lng: parseFloat(statusUpdate.dlongitude)
            });
          }
          
          // Show toast notification with visual feedback
          const statusMessages = {
            "A": { msg: "✅ Driver has accepted your booking!", type: "success" },
            "EN": { msg: "🚗 Driver is on the way to pickup!", type: "info" },
            "IP": { msg: "🏠 You've been picked up! Heading to destination", type: "success" },
            "CO": { msg: "🎉 Trip completed successfully!", type: "success" },
            "C": { msg: "❌ Booking has been cancelled", type: "error" }
          };
          const notification = statusMessages[newStatus];
          if (notification) {
            toast[notification.type](notification.msg, {
              position: "top-center",
              autoClose: 4000,
            });
          }
        }
      });
      
      // Cleanup on unmount - keep booking WS alive for other employee pages
      return () => {
        clearInterval(checkConnection);
        // Don't disconnect bookingWS - keep alive for other employee pages
      };
    }
  }, [authKey, bid, vId]);

  // Subscribe to vehicle location updates
  useEffect(() => {
    if (!vId || vId === "Loading...") return;

    console.log(`[TrackBooking] Subscribing to vehicle: ${vId}, Driver: ${dSNo}`);
    
    const unsub = trackingWS.subscribe(vId, (payload) => {
      console.log(`[TrackBooking] Received location update for ${vId}:`, payload);
      if (payload && payload.latitude !== undefined && payload.longitude !== undefined) {
        const newLat = parseFloat(payload.latitude);
        const newLng = parseFloat(payload.longitude);
        
        // 🔴 SIMPLIFIED: Only update driver position marker, don't build trail
        // Pre-calculated routes are already shown based on status
        setCurrentPos({ lat: newLat, lng: newLng });
        setLastUpdate(new Date());
      }
    });

    return () => {
      console.log(`[TrackBooking] Unsubscribing from vehicle: ${vId}`);
      if (typeof unsub === "function") unsub();
    };
  }, [vId]); // 🔴 REMOVED bookingStatus dependency to prevent unnecessary resubscriptions

  // 🔴 NEW: Polling fallback - fetch fresh status from API every 10 seconds
  // This ensures the page stays in sync even if WebSocket messages are missed
  useEffect(() => {
    if (!bid || isLoading) return;
    
    const pollStatus = async () => {
      try {
        const bookings = await getBookings();
        
        const booking = bookings.find(b => b.bId?.toString() === bid.toString());
        if (booking && booking.status !== bookingStatus) {
          console.log(`[TrackBooking] 🔄 Poll detected status change: ${bookingStatus} → ${booking.status}`);
          setBookingStatus(booking.status);
          
          // Update driver location if available
          if (booking.dlatitude && booking.dlongitude) {
            setCurrentPos({
              lat: parseFloat(booking.dlatitude),
              lng: parseFloat(booking.dlongitude)
            });
          }
          
          // Show toast for status change detected via polling
          const statusMessages = {
            "A": "✅ Driver has accepted!",
            "EN": "🚗 Driver is on the way!",
            "IP": "🏠 Trip in progress!",
            "CO": "🎉 Trip completed!",
            "C": "❌ Booking cancelled"
          };
          
          if (statusMessages[booking.status]) {
            toast.info(statusMessages[booking.status], {
              position: "top-center",
              autoClose: 3000,
            });
          }
        }
      } catch (err) {
        console.error("[TrackBooking] Poll error:", err);
      }
    };
    
    // Poll every 10 seconds
    const pollInterval = setInterval(pollStatus, 10000);
    
    return () => clearInterval(pollInterval);
  }, [bid, bookingStatus, isLoading]);

  // 🔴 DYNAMIC: Calculate distance remaining based on current position
  // This updates in real-time as driver location changes
  const calculateRemainingDistance = () => {
    // If trip is completed or cancelled, show 0
    if (bookingStatus === "CO" || bookingStatus === "COMPLETED" || bookingStatus === "CANCELLED" || bookingStatus === "CANCELED") {
      return "0";
    }
    
    if (!currentPos || !end) return "—";
    
    // If trip is in progress (heading to destination), calculate from current position to destination
    if (bookingStatus === "IP" || bookingStatus === "INPROGRESS") {
      const remaining = haversine(currentPos.lat, currentPos.lng, end[0], end[1]);
      return remaining.toFixed(2);
    }
    
    // If en route to pickup, calculate from current position to pickup
    if (bookingStatus === "A" || bookingStatus === "ACTIVE" || bookingStatus === "EN" || bookingStatus === "ENROUTE") {
      const remaining = haversine(currentPos.lat, currentPos.lng, start[0], start[1]);
      return remaining.toFixed(2);
    }
    
    // For other statuses, return the pre-calculated distance
    return distance;
  };

  // 🔴 DYNAMIC: Calculate ETA remaining based on speed
  const calculateRemainingETA = () => {
    // If trip is completed or cancelled, show 0
    if (bookingStatus === "CO" || bookingStatus === "COMPLETED" || bookingStatus === "CANCELLED" || bookingStatus === "CANCELED") {
      return "0";
    }
    
    if (!currentPos) return duration;
    
    const remainingDistance = parseFloat(calculateRemainingDistance());
    if (!remainingDistance || remainingDistance === 0) return "—";
    
    // Assume average speed of 40 km/h in urban areas
    const avgSpeed = 40;
    const remainingMinutes = Math.ceil((remainingDistance / avgSpeed) * 60);
    
    return remainingMinutes;
  };

  // Helper function to determine target location based on booking status

  // 🔴 SIMPLIFIED: Helper function to get human-readable booking status
  const getBookingStatusLabel = () => {
    const status = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
    if (status === "P" || status === "PENDING") {
      return "⏳ Pending";
    } else if (status === "A" || status === "ACTIVE") {
      return "✅ Accepted";
    } else if (status === "EN" || status === "ENROUTE") {
      return "🚗 Driver Coming";
    } else if (status === "IP" || status === "INPROGRESS") {
      return "🚦 Trip Active";
    } else if (status === "CO" || status === "COMPLETED") {
      return "✅ Trip Completed";
    } else if (status === "C" || status === "CANCELLED" || status === "CANCELED") {
      return "❌ Cancelled";
    }
    return "⏳ Loading...";
  };

  // Helper to check if trip is finished (completed or cancelled)
  const isTripFinished = () => {
    const status = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
    return status === "CO" || status === "COMPLETED" || status === "C" || status === "CANCELLED" || status === "CANCELED";
  };

  // Helper to check if tracking should be active
  const isTrackingActive = () => {
    const status = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
    return status === "EN" || status === "ENROUTE" || status === "IP" || status === "INPROGRESS";
  };

  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get vehicle status message based on booking state
  const getVehicleStatusMessage = () => {
    const status = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
    if (status === "P" || status === "PENDING") {
      return "Waiting for driver acceptance...";
    } else if (status === "A" || status === "ACTIVE") {
      return "🚗 Driver has accepted your booking";
    } else if (status === "EN" || status === "ENROUTE") {
      return "📍 Driver is heading to your pickup location";
    } else if (status === "IP" || status === "INPROGRESS") {
      return "🏠 You're on your way to the destination";
    } else if (status === "CO" || status === "COMPLETED") {
      return "✅ Trip completed successfully";
    } else if (status === "CANCELLED" || status === "CANCELED") {
      return "❌ Booking has been cancelled";
    }
    return "Tracking active...";
  };

  // Get destination message based on status
  const getDestinationMessage = () => {
    const status = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
    const remainingDist = calculateRemainingDistance();
    const remainingEta = calculateRemainingETA();
    
    if (status === "P" || status === "PENDING") {
      return "Waiting for driver to accept your booking";
    } else if (status === "A" || status === "ACTIVE") {
      return "Driver will start heading to you soon";
    } else if (status === "EN" || status === "ENROUTE") {
      return `📍 Distance to pickup: ${remainingDist} km • ETA: ${remainingEta} min`;
    } else if (status === "IP" || status === "INPROGRESS") {
      return `🎯 Distance to destination: ${remainingDist} km • ETA: ${remainingEta} min`;
    } else if (status === "CO" || status === "COMPLETED") {
      return "Thank you for using our service!";
    } else if (status === "C" || status === "CANCELLED" || status === "CANCELED") {
      return "This booking was cancelled";
    }
    return "";
  };

  const center = currentPos ? [currentPos.lat, currentPos.lng] : start || DEFAULT_MAP_CENTER;

  // 🔴 NEW: Loading state UI
  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* <EmployeeHeader /> */}
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-xl font-semibold text-indigo-800">Loading booking details...</p>
            <p className="text-gray-600 mt-2">Fetching latest data from server</p>
          </div>
        </main>
        {/* <EmployeeFooter /> */}
      </div>
    );
  }

  // 🔴 NEW: Error state UI
  if (loadError) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* <EmployeeHeader /> */}
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center p-8 bg-white rounded-2xl shadow-xl max-w-md">
            <div className="text-6xl mb-4">❌</div>
            <p className="text-xl font-semibold text-red-600">{loadError}</p>
            <p className="text-gray-600 mt-2">Please try again or go back to bookings</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              🔄 Retry
            </button>
          </div>
        </main>
        {/* <EmployeeFooter /> */}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* <EmployeeHeader /> */}

      <main className="flex-1 px-4 py-6">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Beautiful Header */}
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border border-white/30">
            <h1 className="text-4xl font-bold text-center text-indigo-900 mb-6">
              Live Tracking • Booking #{bid}
            </h1>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {/* Distance Card - Different content based on status */}
              <div className={`text-white p-6 rounded-2xl shadow-xl text-center ${isTripFinished() ? 'bg-gradient-to-br from-gray-500 to-gray-600' : 'bg-gradient-to-br from-emerald-500 to-teal-600'}`}>
                <p className="text-lg opacity-90">
                  {isTripFinished() ? 'Trip Summary' : 'Distance Remaining'}
                </p>
                <p className="text-4xl font-bold mt-3">
                  {isTripFinished() ? '✓ Done' : `${calculateRemainingDistance()} km`}
                </p>
              </div>

              {/* ETA Card - Different content based on status */}
              <div className={`text-white p-6 rounded-2xl shadow-xl text-center ${isTripFinished() ? 'bg-gradient-to-br from-gray-600 to-gray-700' : 'bg-gradient-to-br from-blue-600 to-indigo-700'}`}>
                <p className="text-lg opacity-90">
                  {isTripFinished() ? 'Journey' : 'Est. Time'}
                </p>
                <p className="text-4xl font-bold mt-3">
                  {isTripFinished() ? 'Complete' : `${calculateRemainingETA()} min`}
                </p>
              </div>

              {/* Status Card - Always shows current status */}
              <div className={`text-white p-6 rounded-2xl shadow-xl text-center ${
                isTripFinished() 
                  ? 'bg-gradient-to-br from-green-600 to-green-700' 
                  : isTrackingActive() 
                    ? 'bg-gradient-to-br from-purple-600 to-pink-600' 
                    : 'bg-gradient-to-br from-amber-500 to-orange-600'
              }`}>
                <p className="text-lg opacity-90">Status</p>
                <p className="text-3xl font-bold mt-3">
                  {getBookingStatusLabel()}
                </p>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className={`mt-8 rounded-2xl p-6 text-center ${
              isTripFinished() 
                ? 'bg-gradient-to-r from-gray-100 to-gray-200' 
                : 'bg-gradient-to-r from-indigo-100 to-purple-100'
            }`}>
              <p className="text-2xl font-bold text-indigo-900">
                Vehicle: <span className="text-purple-800">{vId}</span>
              </p>
              <p className="text-lg text-gray-700 mt-2">
                {getVehicleStatusMessage()}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {getDestinationMessage()}
              </p>
              {/* WebSocket Connection Status - Only show when tracking should be active */}
              {!isTripFinished() && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <span className={`inline-block w-3 h-3 rounded-full ${
                    isTrackingActive() 
                      ? (wsConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500')
                      : 'bg-yellow-500'
                  }`}></span>
                  <span className={`text-sm font-medium ${
                    isTrackingActive()
                      ? (wsConnected ? 'text-green-700' : 'text-red-700')
                      : 'text-yellow-700'
                  }`}>
                    {isTrackingActive() 
                      ? (wsConnected ? 'Live Tracking Active' : 'Connecting...') 
                      : 'Waiting for trip to start'
                    }
                  </span>
                </div>
              )}
              {isTrackingActive() && lastUpdate && (
                <p className="text-xs text-gray-500 mt-1">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>

          {/* Live Map - Responsive with proper constraints */}
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white w-full z-10" style={{ height: "50vh", maxHeight: "55vh", minHeight: "300px" }}>
            <style>{`
              .leaflet-container { z-index: 1 !important; }
              .leaflet-popup { z-index: 2 !important; }
              .leaflet-control { z-index: 2 !important; }
            `}</style>
           <MapContainer
                  center={DEFAULT_CENTER}
                  zoom={17} style={{ height: "100%", width: "100%", zIndex: 1 }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <RecenterButton position={center} zoom={14} />
              <MapController center={center} />

              {/* 🔴 FIXED: Always show pickup & destination markers like DriverTrip - prevents disappearing map elements */}
              {/* Pickup & Drop - Always visible for consistent user experience */}
              {getMapMarkers().showPickup && start && (
                <Marker position={start} icon={startIcon}>
                  <Popup><strong>📍 Pickup Location</strong></Popup>
                </Marker>
              )}
              {getMapMarkers().showDestination && end && (
                <Marker position={end} icon={endIcon}>
                  <Popup><strong>🎯 Destination Location</strong></Popup>
                </Marker>
              )}

              {/* 🔴 EXACTLY LIKE DRIVERTRIP: Simple show/hide based on status */}
              {/* Debug: Log current status and route lengths */}
              {console.log(`[TrackBooking MAP] Status: "${bookingStatus}", RouteToPickup: ${routeToPickup.length}, RouteToDestination: ${routeToDestination.length}`)}
              
              {/* Route to Pickup (blue dashed) - handle all status formats */}
              {(() => {
                const s = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
                const showPickup = s === "A" || s === "ACTIVE" || s === "EN" || s === "ENROUTE";
                return showPickup && routeToPickup.length > 0 ? (
                  <Polyline 
                    positions={routeToPickup} 
                    color="#3b82f6" 
                    weight={6} 
                    opacity={0.9}
                    dashArray="10, 10"
                  />
                ) : null;
              })()}

              {/* Route to Destination (red solid) - handle all status formats */}
              {(() => {
                const s = (bookingStatus || "").toUpperCase().replace(/\s+/g, "");
                const showDest = s === "IP" || s === "INPROGRESS" || s === "CO" || s === "COMPLETED";
                console.log(`[TrackBooking MAP] Normalized status: "${s}", showDest: ${showDest}, routeLen: ${routeToDestination.length}`);
                return showDest && routeToDestination.length > 0 ? (
                  <Polyline 
                    positions={routeToDestination} 
                    color="#ef4444" 
                    weight={6} 
                    opacity={0.9}
                  />
                ) : null;
              })()}

              {/* Fit bounds - simple like DriverTrip */}
              {start && end && (
                <FitBounds bounds={[start, end]} />
              )}

              {/* Live Vehicle */}
              <LiveVehicleMarker position={currentPos} vehicleType={vehicleType} />
            </MapContainer>
          </div>

          {/* Bottom Live Status - Part of layout - Shows appropriate info based on trip status */}
          <div className={`text-white text-center py-4 backdrop-blur-md border-t border-white/20 rounded-b-3xl -mx-4 sm:-mx-6 ${isTripFinished() ? 'bg-gray-800/90' : 'bg-black/90'}`}>
            <div className="flex items-center justify-center gap-3 flex-wrap px-4">
              {/* Show different indicators based on trip status */}
              {isTripFinished() ? (
                <>
                  <span className="inline-block w-2 h-2 rounded-full bg-gray-400"></span>
                  <p className="text-lg font-medium">
                    {getBookingStatusLabel()}
                  </p>
                </>
              ) : isTrackingActive() ? (
                <>
                  <span className={`inline-block w-2 h-2 rounded-full ${wsConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></span>
                  <p className="text-lg font-medium">
                    {wsConnected ? '🟢 Live Tracking Active' : '🔴 Reconnecting...'} 
                  </p>
                  <span className="text-gray-400">•</span>
                  <p className="text-lg font-medium">
                    {getBookingStatusLabel()}
                  </p>
                </>
              ) : (
                <>
                  <span className="inline-block w-2 h-2 rounded-full bg-yellow-400"></span>
                  <p className="text-lg font-medium">
                    {getBookingStatusLabel()}
                  </p>
                  <span className="text-gray-400">•</span>
                  <p className="text-lg font-medium">Waiting for driver</p>
                </>
              )}
            </div>
            
            {/* Show tracking details only when trip is active (ENROUTE or INPROGRESS) */}
            {isTrackingActive() && currentPos && lastUpdate && (
              <div className="mt-2 text-sm opacity-85 space-y-1 px-4">
                <p>
                  Last Update: {lastUpdate.toLocaleTimeString()} | Position: {currentPos.lat.toFixed(4)}, {currentPos.lng.toFixed(4)}
                </p>
                <p>
                  Distance Remaining: <span className="font-bold text-yellow-400">{calculateRemainingDistance()} km</span> • Est. Time: <span className="font-bold text-cyan-400">{calculateRemainingETA()} min</span>
                </p>
              </div>
            )}
            
            {/* Show completion message when trip is finished */}
            {isTripFinished() && (
              <div className="mt-2 text-sm opacity-85 px-4">
                <p className="text-gray-300">Trip has ended. Thank you for using our service!</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* <EmployeeFooter /> */}
    </div>
  );
}
